import React, { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { EXRLoader } from 'three/examples/jsm/loaders/EXRLoader.js';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader.js';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { Box } from '@mui/material';
import { isMobileWebBrowser, mobile, isIOS, isAndroid } from 'src/utils/platformUtils';
import {
  sanitizeThoughtText,
  updateSignTextTexture,
  createSignTextCanvasTexture,
  SPRITE_SHIFT_ALONG_PANEL_DOWN,
  PANEL_TEXT_INSET,
  TEXT_PLANE_SURFACE_OFFSET,
} from './CharacterSign2';
import { getVegaHtmlAudioTapDelaySec, isVegaHtmlAudioTap, isVegaLipSyncEnabled } from 'src/lipsync/vegaLipSync';
import { getVegaFaceRenderer } from 'src/lipsync/vegaFaceRender';

const isMobile = isMobileWebBrowser() || mobile || isIOS || isAndroid;

/** Single-scene pipeline: room + newQuan + Left Sign. Camera comes from background GLB (Camera.003). */
const URL_BACKGROUND = '/character/background-and-cameras.glb';
const URL_CHARACTER = '/character/newQuan.glb';
const URL_SIGN = '/character/Left Sign.glb';
const URL_LOADING_DOTS = '/character/Loading dots.glb';
const URL_FACE_JSON = '/character/face.json';
const URL_ENV_HDR = '/character/brown_photostudio_02_1k.exr';
const ASSET_MAX_RETRIES = 3;
const ASSET_RETRY_BASE_DELAY_MS = 350;
// Debug-only switch (KEEP IT IN FALSE, IF YOU PUT THIS TO TRUE IT WILL BREAK QUAN): force all retry attempts to fail.
const DEBUG_FORCE_FAIL_ALL_RETRY_ATTEMPTS = false;

const GRID_SIZE = 48; //with the old face.json, it was 64 here
const LED_COUNT = GRID_SIZE * GRID_SIZE;

const VISUAL_SETTINGS = {
  bloomThreshold: 1.0,
  bloomStrength: 0.0,
  bloomRadius: 2.0,
  ledEmissiveIntensity: 10, //before, it was 29.65
  ledColor: 0xf6db84, // before, it was 0xf6db84
  ledEmissiveColor: 0x9a3d00,
  facePosition: { x: 0, y: 3.5, z: 10 },
  // Keep the same on-visor footprint after moving from 64x64 to 48x48 face matrices.
  // faceScale: { x: 10, y: 12, z: 8 }, before, with old face.json (with 64 grid size)
  faceScale: { x: 18, y: 20, z: 10 },
};

interface LipSyncMouthCue {
  start: number;
  end: number;
  /** OVR viseme: sil, PP, FF, TH, DD, kk, CH, SS, nn, RR, aa, E, ih, oh, ou */
  value: string;
}

interface VegaTrackEvent {
  t: number;
  viseme: string;
  intensity?: number;
  level?: number;
}

interface LipSyncData {
  mouthCues: LipSyncMouthCue[];
  vegaTrack?: VegaTrackEvent[];
  metadata?: {
    soundFile: string;
    duration: number;
  };
}

interface FaceData {
  eyes: Record<string, number[]>;
  mouths: Record<string, number[]>;
}

export interface CharacterEmbed2Ref {
  /** @param data accepts LipSyncData (OVR visemes). Legacy name kept for caller compatibility. */
  setLipSyncData: (data: LipSyncData) => void;
  setAudioElement: (audio: HTMLAudioElement) => void;
  playAnimation: (name: string) => void;
  setIsTalking: (talking: boolean) => void;
  setPlaybackStartTime: (startTime: number) => void;
}

interface CharacterEmbed2Props {
  height?: number | string;
  backgroundColor?: string;
  onLoad?: () => void;
  onError?: (error: Error) => void;
  /** Called right before a background-resume scene rebuild so the parent can show a loader. */
  onBeforeRebuild?: () => void;
  /** When true, sign mesh plays in / idle; when false, anim out (keep mounted from parent for timing). */
  showSign?: boolean;
  signText?: string;
}

function pickCamera003(root: THREE.Object3D): THREE.PerspectiveCamera | null {
  root.updateMatrixWorld(true);
  const cams: THREE.PerspectiveCamera[] = [];
  root.traverse((obj) => {
    const c = obj as THREE.PerspectiveCamera;
    if (c.isPerspectiveCamera) cams.push(c);
  });
  const byName003 = cams.find((c) => (c.name || '').includes('003'));
  if (byName003) return byName003;
  const byLeftIn = cams.find((c) => (c.name || '').toLowerCase().includes('left in'));
  if (byLeftIn) return byLeftIn;
  return cams[0] ?? null;
}

function pickAnyPerspectiveCamera(root: THREE.Object3D): THREE.PerspectiveCamera | null {
  let found: THREE.PerspectiveCamera | null = null;
  root.traverse((obj) => {
    const c = obj as THREE.PerspectiveCamera;
    if (c.isPerspectiveCamera && !found) found = c;
  });
  return found;
}

function syncCameraFromGltf(
  dest: THREE.PerspectiveCamera,
  src: THREE.PerspectiveCamera,
  aspect: number
): void {
  src.updateWorldMatrix(true, false);
  const m = src.matrixWorld;
  const pos = new THREE.Vector3();
  const quat = new THREE.Quaternion();
  const scl = new THREE.Vector3();
  m.decompose(pos, quat, scl);
  dest.position.copy(pos);
  dest.quaternion.copy(quat);
  dest.scale.set(1, 1, 1);
  dest.fov = src.fov;
  dest.near = src.near;
  dest.far = src.far;
  dest.aspect = aspect;
  dest.filmOffset = 0;
  dest.updateProjectionMatrix();
}

// --- Animation helpers (newQuan uses "mixamorig:Bone"; ":" breaks THREE.PropertyBinding track parsing) ---

function renameObjectNamesColonToUnderscore(root: THREE.Object3D): void {
  root.traverse((obj) => {
    if (obj.name && obj.name.includes(':')) {
      obj.name = obj.name.replace(/:/g, '_');
    }
  });
}

/** Mixamo FBX: mixamorigHips → mixamorig_Hips (matches renamed GLB bones). Order: mixamorig1 before mixamorig. */
function remapMixamoFbxTrackName(trackName: string): string {
  const dot = trackName.indexOf('.');
  const node = dot >= 0 ? trackName.slice(0, dot) : trackName;
  const suffix = dot >= 0 ? trackName.slice(dot) : '';
  let n = node.replace(/^mixamorig1(?=[A-Z])/g, 'mixamorig1_');
  n = n.replace(/^mixamorig(?=[A-Z])/g, 'mixamorig_');
  const leftFootFix: Record<string, string> = {
    mixamorig_LeftFoot: 'mixamorig1_LeftFoot',
    mixamorig_LeftToeBase: 'mixamorig1_LeftToeBase',
  };
  if (leftFootFix[n]) n = leftFootFix[n];
  return n + suffix;
}

function sanitizeGltfClipTrackColons(clip: THREE.AnimationClip): void {
  clip.tracks.forEach((t) => {
    if (t.name.includes(':')) t.name = t.name.replace(/:/g, '_');
  });
}

/**
 * Keep tracks that PropertyBinding can resolve. For skinned GLBs, bones are resolved via
 * Skeleton.getBoneByName ONLY when AnimationMixer root is a SkinnedMesh (see THREE.PropertyBinding.findNode).
 */
function filterAnimationTracksForRig(
  clip: THREE.AnimationClip,
  mixerRoot: THREE.Object3D,
  skinnedForBones: THREE.SkinnedMesh | null
): void {
  const before = clip.tracks.length;
  const skel = skinnedForBones?.skeleton;
  clip.tracks = clip.tracks.filter((track) => {
    const dot = track.name.indexOf('.');
    if (dot <= 0) return false;
    const nodeName = track.name.slice(0, dot);
    if (skel && skel.getBoneByName(nodeName)) return true;
    return mixerRoot.getObjectByName(nodeName) !== undefined;
  });
  if (before > clip.tracks.length) {
    console.warn(
      `[Character2] Removed ${before - clip.tracks.length} tracks (no bone/node on this rig)`
    );
  }
}

/** Prefer the SkinnedMesh with the most bones (Lombardi shares one skeleton across mesh parts). */
function pickPrimarySkinnedMesh(root: THREE.Object3D): THREE.SkinnedMesh | null {
  let best: THREE.SkinnedMesh | null = null;
  let bestN = 0;
  root.traverse((obj) => {
    const sm = obj as THREE.SkinnedMesh;
    if (sm.isSkinnedMesh && sm.skeleton?.bones?.length) {
      const n = sm.skeleton.bones.length;
      if (n > bestN) {
        bestN = n;
        best = sm;
      }
    }
  });
  return best;
}

const CharacterEmbed2 = forwardRef<CharacterEmbed2Ref, CharacterEmbed2Props>(
  (
    {
      height = 200,
      backgroundColor = '#000000',
      onLoad,
      onError,
      onBeforeRebuild,
      showSign = false,
      signText = '',
    },
    ref
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const sceneRef = useRef<THREE.Scene | null>(null);
    const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
    const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
    const composerRef = useRef<EffectComposer | null>(null);
    const bloomPassRef = useRef<UnrealBloomPass | null>(null);
    const mixerRef = useRef<THREE.AnimationMixer | null>(null);
    const animationsRef = useRef<Record<string, THREE.AnimationAction>>({});
    const clockRef = useRef(new THREE.Clock());
    const ledMeshRef = useRef<THREE.InstancedMesh | null>(null);
    const headBoneRef = useRef<THREE.Object3D | null>(null);
    const dummyRef = useRef(new THREE.Object3D());
    const animationFrameRef = useRef(0);

    const sourceCameraRef = useRef<THREE.PerspectiveCamera | null>(null);

    // Camera animation: background GLB contains "Camera Left in" / "Camera Left out" + their animation clips
    const bgMixerRef = useRef<THREE.AnimationMixer | null>(null);
    const cameraLeftInRef = useRef<THREE.PerspectiveCamera | null>(null);
    const cameraLeftOutRef = useRef<THREE.PerspectiveCamera | null>(null);
    const camClipLeftInRef = useRef<THREE.AnimationClip | null>(null);
    const camClipLeftOutRef = useRef<THREE.AnimationClip | null>(null);

    const signMixerRef = useRef<THREE.AnimationMixer | null>(null);
    const signRootRef = useRef<THREE.Object3D | null>(null);
    const signPanelRef = useRef<THREE.SkinnedMesh | THREE.Mesh | null>(null);
    const signTextPlaneRef = useRef<THREE.Mesh | null>(null);
    const signTextCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const signTextTextureRef = useRef<THREE.CanvasTexture | null>(null);
    const signActionsRef = useRef<Record<string, THREE.AnimationAction>>({});
    const prevSignVisibleRef = useRef(false);
    const signReadyRef = useRef(false);
    const loadingDotsRootRef = useRef<THREE.Object3D | null>(null);
    const loadingDotsMixerRef = useRef<THREE.AnimationMixer | null>(null);
    const signAnimInFinishedHandlerRef = useRef<
      ((e: { action: THREE.AnimationAction }) => void) | null
    >(null);

    const lipsyncDataRef = useRef<LipSyncData | null>(null);
    const faceDataRef = useRef<FaceData | null>(null);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const isTalkingRef = useRef(false);
    const speechEndTimeRef = useRef(-1);
    const playbackStartTimeRef = useRef<number | null>(null);
    const htmlAudioPlayWallRef = useRef<number | null>(null);
    const lipsHoldUntilRef = useRef<number | null>(null);
    const audioEndHandlerRef = useRef<(() => void) | null>(null);
    const lastDefaultVisemeLogRef = useRef(0);
    const lastSyncBranchLogRef = useRef(0);
    const lastSyncBranchRef = useRef<string>('');
    const lipsyncDiagFiredRef = useRef(false);

    // Background resume / WebGL context recovery: bump sceneVersion to trigger full teardown + rebuild.
    const [sceneVersion, setSceneVersion] = useState(0);
    const contextLostRef = useRef(false);
    const rebuildTimerRef = useRef<number | null>(null);

    const showSignRef = useRef(showSign);
    const signTextPropRef = useRef(signText);
    showSignRef.current = showSign;
    signTextPropRef.current = signText;

    // When audio is playing, the sign is temporarily hidden; restored when audio ends.
    const signSuppressedByAudioRef = useRef(false);

    // Pre-allocated for sign text plane follow (same math as CharacterSign)
    const boxPrecise = useRef(new THREE.Box3());
    const centerVec = useRef(new THREE.Vector3());
    const sizeVec = useRef(new THREE.Vector3());
    const panelDownWorld = useRef(new THREE.Vector3());
    const v0 = useRef(new THREE.Vector3());
    const v1 = useRef(new THREE.Vector3());
    const v2 = useRef(new THREE.Vector3());
    const edge1 = useRef(new THREE.Vector3());
    const edge2 = useRef(new THREE.Vector3());
    const faceNormal = useRef(new THREE.Vector3());
    const faceUp = useRef(new THREE.Vector3());
    const faceRight = useRef(new THREE.Vector3());
    const toCamera = useRef(new THREE.Vector3());
    const worldUp = useRef(new THREE.Vector3(0, 1, 0));
    const rotMat = useRef(new THREE.Matrix4());
    const shiftVec = useRef(new THREE.Vector3());

    useImperativeHandle(ref, () => ({
      setLipSyncData: (data: LipSyncData) => {
        const cues = data?.mouthCues ?? [];
        const count = cues.length;
        let timeRange = 'n/a';
        if (count > 0) {
          const starts = cues.map((c) => Number(c.start));
          const ends = cues.map((c) => Number(c.end));
          const firstStart = Math.min(...starts);
          const lastEnd = Math.max(...ends);
          timeRange = `${firstStart.toFixed(2)}s–${lastEnd.toFixed(2)}s`;
        }
        // console.log('[Character2] setLipSyncData:', count, 'cues,', timeRange);
        if (data?.vegaTrack?.length) {
          // console.log(`[LipSync] VEGA draw track received (${data.vegaTrack.length} events, morph+levels)`);
        }
        lipsyncDataRef.current = data;
        lipsyncDiagFiredRef.current = false;
      },
      setAudioElement: (audio: HTMLAudioElement) => {
        // Same element is already bound (bind-before-play on Capacitor). Keep the heard clock.
        if (audioRef.current === audio) return;
        if (audioRef.current && audioEndHandlerRef.current) {
          audioRef.current.removeEventListener('ended', audioEndHandlerRef.current);
        }
        audioRef.current = audio;
        // audio.playbackRate = 0.9;
        playbackStartTimeRef.current = null;
        lipsHoldUntilRef.current = null;
        htmlAudioPlayWallRef.current = null;
        const endHandler = () => {
          // Speaker buffer can still play ~0.4s after ended/pause.
          lipsHoldUntilRef.current = performance.now() / 1000 + 0.45;
          htmlAudioPlayWallRef.current = null;
        };
        audioEndHandlerRef.current = endHandler;
        audio.addEventListener('ended', endHandler);
        audio.addEventListener('pause', () => {
          lipsHoldUntilRef.current = performance.now() / 1000 + 0.45;
          htmlAudioPlayWallRef.current = null;
        });
        audio.addEventListener('play', () => {
          // Vega iOS tap: wait for first viseme so talking does not start on silent preroll.
          if (!isVegaHtmlAudioTap(audio)) isTalkingRef.current = true;
        });
        // 'playing' = samples flowing. 'play' fires on play() and would start Vega before URL audio.
        const armHtmlAudioClock = () => {
          if (isVegaHtmlAudioTap(audio)) return;
          isTalkingRef.current = true;
          if (htmlAudioPlayWallRef.current == null) {
            htmlAudioPlayWallRef.current = performance.now() / 1000 - (audio.currentTime || 0);
          }
        };
        // Capacitor + mobile web URL: 'playing' fires while currentTime is still 0 and the speaker is silent.
        // Arm the mouth only once the playhead moves, so visemes and audio start together.
        // Desktop web is already synced (do not wait). iOS tap returns above.
        let playheadPoll = 0;
        const markHtmlAudioPlaying = () => {
          if (isVegaHtmlAudioTap(audio)) return;
          const waitForPlayhead = (mobile || isMobileWebBrowser()) && isVegaLipSyncEnabled();
          if (!waitForPlayhead || (audio.currentTime || 0) > 0) {
            armHtmlAudioClock();
            return;
          }
          const started = performance.now();
          const token = ++playheadPoll;
          const poll = () => {
            if (playheadPoll !== token || audioRef.current !== audio || audio.paused) return;
            if ((audio.currentTime || 0) > 0) {
              armHtmlAudioClock();
              return;
            }
            // Capacitor: iOS can keep currentTime at 0 while audible. Mobile web GCS buffer is 1–2s — do not arm early.
            const giveUpMs = mobile ? 400 : 4000;
            if (performance.now() - started > giveUpMs) {
              armHtmlAudioClock();
              return;
            }
            requestAnimationFrame(poll);
          };
          requestAnimationFrame(poll);
        };
        audio.addEventListener('playing', markHtmlAudioPlaying);
        if (!audio.paused && audio.played.length > 0) markHtmlAudioPlaying();
      },
      playAnimation: (name: string) => {
        const animations = animationsRef.current;
        const mixer = mixerRef.current;
        if (!mixer || !animations[name]) {
          console.warn(`[Character2] Animation "${name}" not found`);
          return;
        }
        Object.values(animations).forEach((action) => action.fadeOut(0.3));
        const action = animations[name];
        action.reset();
        action.fadeIn(0.3);
        action.play();
      },
      setPlaybackStartTime: (startTime: number) => {
        playbackStartTimeRef.current = startTime;
      },
      setIsTalking: (talking: boolean) => {
        isTalkingRef.current = talking;
        if (!talking) {
          speechEndTimeRef.current = performance.now() / 1000;
          playbackStartTimeRef.current = null;
          htmlAudioPlayWallRef.current = null;
          lipsHoldUntilRef.current = null;
          lipsyncDataRef.current = null; // Close mouth; do not hold the last viseme after audio stops.
        }
        // Body stays in Idle; face/mouth still animates via updateMouth lipsync.
      },
    }));

    const updateMouth = useCallback((time: number) => {
      const faceData = faceDataRef.current;
      const ledMesh = ledMeshRef.current;
      const audio = audioRef.current;
      const lipsyncData = lipsyncDataRef.current;

      // if (true) return;
      if (!faceData || !ledMesh) return;

      let eyeKey = 'NEUTRAL';
      let mouthKey = 'sil';
      const now = performance.now() / 1000;
      const htmlAudioTime = (el: HTMLAudioElement): number => {
        const reported = el.currentTime || 0;
        const wallStart = htmlAudioPlayWallRef.current;
        let t = reported;
        if (!el.paused && wallStart != null && !isVegaHtmlAudioTap(el)) {
          const elapsed = now - wallStart;
          // Capacitor Vega URL (team-name/agents): currentTime is often 0 at start — use heard clock immediately.
          // After 0.15s trust currentTime so wall-clock does not finish the viseme track before the last word.
          const vegaUrlOnCap = (mobile || isMobileWebBrowser()) && isVegaLipSyncEnabled() && !!lipsyncDataRef.current?.vegaTrack?.length;
          if (vegaUrlOnCap) {
            // Stay on currentTime. Wall elapsed while the playhead is still ~0 opens the mouth before the speaker.
            t = reported;
          } else if (reported < 0.15 && elapsed - reported > 0.12) {
            t = elapsed;
          }
        }
        // iOS Vega tap delays speakers; look up visemes on the heard clock (desktop URL/PCM unchanged).
        const tapDelay = getVegaHtmlAudioTapDelaySec(el);
        if (tapDelay > 0) t = Math.max(0, t - tapDelay);
        return t;
      };

      if (
        playbackStartTimeRef.current !== null &&
        lipsyncData?.mouthCues &&
        lipsyncData.mouthCues.length > 0
      ) {
        const elapsedSinceStart = now - playbackStartTimeRef.current;
        const numericCues = lipsyncData.mouthCues.map((c) => ({
          start: Number(c.start),
          end: Number(c.end),
        }));
        const maxEnd = Math.max(...numericCues.map((c) => c.end), 0);
        const lastEndSeconds = maxEnd > 60 ? maxEnd / 1000 : maxEnd;
        const htmlAudioPlaying = audio && !audio.paused;
        if (!htmlAudioPlaying && elapsedSinceStart > lastEndSeconds + 1.0) {
          const wasTalking = isTalkingRef.current;
          if (wasTalking) {
            isTalkingRef.current = false;
            speechEndTimeRef.current = now;
            playbackStartTimeRef.current = null;
          }
        }
      }

      const htmlUrlAudioActive =
        audio &&
        !audio.paused &&
        (htmlAudioPlayWallRef.current != null || (audio.currentTime || 0) > 0.05);
      const isCurrentlyTalking =
        playbackStartTimeRef.current !== null || !!htmlUrlAudioActive;

      if (isCurrentlyTalking) {
        let currentTime = 0;
        if (playbackStartTimeRef.current !== null) {
          currentTime = now - playbackStartTimeRef.current;
        } else if (audio) {
          currentTime = htmlAudioTime(audio);
        }
        let syncBranch: string;
        if (lipsyncData?.mouthCues && playbackStartTimeRef.current !== null) syncBranch = 'PCM';
        else if (lipsyncData?.mouthCues && audio) syncBranch = 'audio';
        else if (lipsyncData?.mouthCues && !audio && playbackStartTimeRef.current === null)
          syncBranch = 'fallback_no_playback';
        else syncBranch = 'fallback_no_lipsync';
        if (now - lastSyncBranchLogRef.current >= 1.5 || lastSyncBranchRef.current !== syncBranch) {
          lastSyncBranchLogRef.current = now;
          lastSyncBranchRef.current = syncBranch;
          // console.log(
          //   `[LipsyncTrace] Character2 updateMouth syncBranch=${syncBranch} currentTime=${currentTime.toFixed(2)}s`
          // );
        }

        // One-time diagnostic per lip sync session
        if (!lipsyncDiagFiredRef.current && lipsyncData?.mouthCues?.length) {
          lipsyncDiagFiredRef.current = true;
          const allStarts = lipsyncData.mouthCues.map((c) => Number(c.start));
          const allEnds = lipsyncData.mouthCues.map((c) => Number(c.end));
          const cueMin = Math.min(...allStarts);
          const cueMax = Math.max(...allEnds);
          const audioDur = audio?.duration ?? 'N/A';
          // console.warn(
          //   `[LipSyncDiag] TIMING CHECK | cues=${lipsyncData.mouthCues.length} cueRange=${cueMin.toFixed(3)}s–${cueMax.toFixed(3)}s | audioDuration=${typeof audioDur === 'number' ? audioDur.toFixed(3) + 's' : audioDur} | syncBranch=${syncBranch} | maxEnd>60=${cueMax > 60}`
          // );
        }

        if (lipsyncData && lipsyncData.mouthCues && (audio || playbackStartTimeRef.current !== null)) {
          const playbackNotStartedYet =
            playbackStartTimeRef.current === null && audio && audio.paused && currentTime < 0.05;
          if (playbackNotStartedYet) {
            mouthKey = 'sil';
          } else {
            let cues = lipsyncData.mouthCues.map((c) => ({
              start: Number(c.start),
              end: Number(c.end),
              value: c.value,
            }));
            const maxEndCue = Math.max(...cues.map((c) => c.end), 0);
            if (maxEndCue > 60) {
              cues = cues.map((c) => ({ ...c, start: c.start / 1000, end: c.end / 1000 }));
            }
            const sortedCues = [...cues].sort((a, b) => a.start - b.start);
            // Bridge gaps between consecutive cues so the mouth holds the current
            // viseme until the next phoneme starts instead of flickering to silence.
            for (let i = 0; i < sortedCues.length - 1; i++) {
              if (sortedCues[i].end < sortedCues[i + 1].start) {
                sortedCues[i] = { ...sortedCues[i], end: sortedCues[i + 1].start };
              }
            }
            const firstCue = sortedCues[0];
            const lastCue = sortedCues[sortedCues.length - 1];
            let cue = null;
            for (let i = 0; i < sortedCues.length; i++) {
              const c = sortedCues[i];
              if (currentTime >= c.start && currentTime < c.end) {
                cue = c;
                break;
              }
              if (c.start > currentTime) break;
            }
            if (cue) {
              mouthKey = cue.value;
            } else {
              const beforeFirst = firstCue && currentTime < firstCue.start;
              const afterLast = lastCue && currentTime > lastCue.end;
              if (afterLast) {
                // Cues exhausted but audio still playing — recycle the phoneme
                // pattern so the mouth keeps moving naturally until audio ends.
                const cueSpan = lastCue.end - firstCue.start;
                if (cueSpan > 0) {
                  const wrappedTime = firstCue.start + ((currentTime - firstCue.start) % cueSpan);
                  for (let j = 0; j < sortedCues.length; j++) {
                    if (wrappedTime >= sortedCues[j].start && wrappedTime < sortedCues[j].end) {
                      mouthKey = sortedCues[j].value;
                      break;
                    }
                  }
                }
              } else if (!beforeFirst) {
                let inGap = false;
                if (sortedCues.length >= 2) {
                  for (let i = 0; i < sortedCues.length - 1; i++) {
                    if (currentTime >= sortedCues[i].end && currentTime < sortedCues[i + 1].start) {
                      inGap = true;
                      break;
                    }
                  }
                }
                if (!inGap) {
                  mouthKey = 'sil';
                }
              }
            }
          }
        } else if (
          lipsyncData &&
          lipsyncData.mouthCues &&
          isTalkingRef.current &&
          !audio &&
          playbackStartTimeRef.current === null
        ) {
          const cueCount = lipsyncData.mouthCues.length;
          if (cueCount > 0) {
            const cycleTime = (time * 8) % cueCount;
            const cueIndex = Math.floor(cycleTime);
            if (lipsyncData.mouthCues[cueIndex]) {
              mouthKey = lipsyncData.mouthCues[cueIndex].value;
            }
          }
        } else if (isTalkingRef.current) {
          const genericVisemes = ['aa', 'DD', 'ih', 'oh', 'PP', 'FF', 'sil'];
          const cycleTime = (time * 6) % genericVisemes.length;
          mouthKey = genericVisemes[Math.floor(cycleTime)];
        }
        if (time % 5 < 0.15) eyeKey = 'BLINK';
      } else if (speechEndTimeRef.current > 0 && now - speechEndTimeRef.current < 3) {
        eyeKey = 'HAPPY';
        mouthKey = 'sil';
      } else {
        mouthKey = 'sil';
        if (time % 5 < 0.15) eyeKey = 'BLINK';
      }

      if (lipsHoldUntilRef.current != null && now >= lipsHoldUntilRef.current) {
        lipsHoldUntilRef.current = null;
        if (!audio || audio.paused) {
          isTalkingRef.current = false;
          speechEndTimeRef.current = now;
        }
      }
      const vegaHold = lipsHoldUntilRef.current != null && now < lipsHoldUntilRef.current;

      const eyeBits = faceData.eyes?.[eyeKey] || faceData.eyes?.['NEUTRAL'] || [];
      let mouthBits: ArrayLike<number> = faceData.mouths?.[mouthKey] || faceData.mouths?.['sil'] || [];
      // Vega renderer: redrawn shapes + SDF morph + volume levels. Azure path keeps the hard swap above.
      if (
        isVegaLipSyncEnabled() &&
        lipsyncData?.vegaTrack &&
        lipsyncData.vegaTrack.length > 0 &&
        (isCurrentlyTalking || vegaHold)
      ) {
        const track = lipsyncData.vegaTrack;
        let vegaTime = 0;
        if (playbackStartTimeRef.current !== null) {
          vegaTime = now - playbackStartTimeRef.current;
        } else if (audio) {
          vegaTime = htmlAudioTime(audio);
        }
        let lastSpeechT: number | null = null;
        for (let i = track.length - 1; i >= 0; i--) {
          if (track[i].viseme && track[i].viseme !== 'sil') {
            lastSpeechT = track[i].t;
            break;
          }
        }
        // Keep the last speech viseme while audio is still coming out of the speakers.
        if (lastSpeechT != null && (vegaHold || (audio && !audio.paused)) && vegaTime > lastSpeechT) {
          vegaTime = lastSpeechT;
        }
        mouthBits = getVegaFaceRenderer().frame(track, vegaTime);
      }
      if (eyeBits.length === 0 && mouthBits.length === 0) return;

      const spacing = 0.015;
      const offset = (GRID_SIZE * spacing) / 2;
      for (let i = 0; i < LED_COUNT; i++) {
        const isActive = eyeBits[i] === 1 || mouthBits[i] === 1;
        const x = (i % GRID_SIZE) * spacing - offset;
        const y = -(Math.floor(i / GRID_SIZE) * spacing - offset);
        dummyRef.current.position.set(x, y, 0);
        dummyRef.current.scale.setScalar(isActive ? 1 : 0);
        dummyRef.current.updateMatrix();
        ledMesh.setMatrixAt(i, dummyRef.current.matrix);
      }
      ledMesh.instanceMatrix.needsUpdate = true;
    }, []);

    const applySignVisibility = useCallback((visible: boolean) => {
      const prev = prevSignVisibleRef.current;
      const root = signRootRef.current;
      const mixer = signMixerRef.current;
      const textPlane = signTextPlaneRef.current;
      const dotsRoot = loadingDotsRootRef.current;
      const ledMesh = ledMeshRef.current;
      const actions = signActionsRef.current;
      const hasThoughtText = !!sanitizeThoughtText(signTextPropRef.current);

      // Face vs dots: same timing as thought text — dots only after Sign Idle Hold (when "Sign Anim in" ends)
      if (!visible) {
        if (dotsRoot) dotsRoot.visible = false;
        if (ledMesh) ledMesh.visible = true;
      } else if (visible && prev) {
        if (dotsRoot) dotsRoot.visible = true;
        if (ledMesh) ledMesh.visible = false;
      } else {
        // visible && !prev: entering — keep LED until Sign Idle (mixer onFinished or immediate idle branch)
        if (dotsRoot) dotsRoot.visible = false;
        if (ledMesh) ledMesh.visible = true;
      }

      const detachSignAnimInFinished = () => {
        const h = signAnimInFinishedHandlerRef.current;
        if (h && mixer) {
          mixer.removeEventListener('finished', h);
        }
        signAnimInFinishedHandlerRef.current = null;
      };

      if (textPlane) {
        if (!visible) {
          textPlane.visible = false;
        } else if (visible && !prev && actions['Sign Anim in']) {
          // Thoughts only after "Sign Anim in" ends (see onFinished → Sign Idle).
          textPlane.visible = false;
        } else {
          textPlane.visible = hasThoughtText;
        }
      }
      if (!root || !mixer) {
        return;
      }

      if (visible && !prev) {
        detachSignAnimInFinished();
        root.visible = true;
        mixer.stopAllAction();

        // Switch camera to "Left in" and play its animation switchState → Thinking)
        if (cameraLeftInRef.current && bgMixerRef.current && camClipLeftInRef.current) {
          sourceCameraRef.current = cameraLeftInRef.current;
          bgMixerRef.current.stopAllAction();
          const camAction = bgMixerRef.current.clipAction(camClipLeftInRef.current);
          camAction.clampWhenFinished = true;
          camAction.setLoop(THREE.LoopOnce, 1);
          camAction.reset().play();
        }

        const animIn = actions['Sign Anim in'];
        const idle = actions['Sign Idle Hold'];
        if (animIn) {
          animIn.reset().play();
          const onFinished = (e: { action: THREE.AnimationAction }) => {
            if (e.action !== animIn) return;
            mixer.removeEventListener('finished', onFinished);
            signAnimInFinishedHandlerRef.current = null;
            if (idle) {
              idle.reset().play();
            }
            const tp = signTextPlaneRef.current;
            if (tp) {
              tp.visible = !!sanitizeThoughtText(signTextPropRef.current);
            }
            const dr = loadingDotsRootRef.current;
            const lm = ledMeshRef.current;
            if (dr) dr.visible = true;
            if (lm) lm.visible = false;
          };
          signAnimInFinishedHandlerRef.current = onFinished;
          mixer.addEventListener('finished', onFinished);
        } else if (idle) {
          idle.reset().play();
          if (dotsRoot) dotsRoot.visible = true;
          if (ledMesh) ledMesh.visible = false;
        } else {
          if (dotsRoot) dotsRoot.visible = true;
          if (ledMesh) ledMesh.visible = false;
        }
      } else if (!visible && prev) {
        detachSignAnimInFinished();
        if (textPlane) textPlane.visible = false;
        mixer.stopAllAction();

        // Switch camera back to "Left out" and play its animation (same as boss's prototype switchState → Idle)
        if (cameraLeftOutRef.current && bgMixerRef.current && camClipLeftOutRef.current) {
          sourceCameraRef.current = cameraLeftOutRef.current;
          bgMixerRef.current.stopAllAction();
          const camAction = bgMixerRef.current.clipAction(camClipLeftOutRef.current);
          camAction.clampWhenFinished = true;
          camAction.setLoop(THREE.LoopOnce, 1);
          camAction.reset().play();
        }

        const animOut = actions['Sign anim Out'];
        if (animOut) {
          animOut.reset().play();
          const onFinished = (e: { action: THREE.AnimationAction }) => {
            if (e.action !== animOut) return;
            mixer.removeEventListener('finished', onFinished);
            root.visible = false;
          };
          mixer.addEventListener('finished', onFinished);
        } else {
          root.visible = false;
        }
      }
      prevSignVisibleRef.current = visible;
    }, []);

    useEffect(() => {
      if (!signReadyRef.current) return;

      // BYPASS/FIX: When the sign needs to appear (new thoughts started), 
      // clear any old stuck audio to ensure the sign isn't blocked forever.
      if (showSign) {
        if (audioRef.current && !audioRef.current.paused) {
          audioRef.current.pause();
        }
        isTalkingRef.current = false;
        playbackStartTimeRef.current = null;
      }

      const quanTalking = isTalkingRef.current || playbackStartTimeRef.current !== null || (audioRef.current && !audioRef.current.paused);
      if (!showSign) {
        // Only clear suppression if audio actually stopped
        if (!quanTalking) signSuppressedByAudioRef.current = false;
        applySignVisibility(false);
        return;
      }
      // showSign=true: block if Quan is still talking
      if (quanTalking) {
        signSuppressedByAudioRef.current = true;
        return;
      }
      signSuppressedByAudioRef.current = false;
      applySignVisibility(true);
    }, [showSign, applySignVisibility]);

    useEffect(() => {
      if (signTextCanvasRef.current && signTextTextureRef.current) {
        updateSignTextTexture(signTextCanvasRef.current, signTextTextureRef.current, signText);
      }
    }, [signText]);

    const updateSignTextPlaneFollow = useCallback(() => {
      const panel = signPanelRef.current;
      const textPlane = signTextPlaneRef.current;
      const cam = cameraRef.current;
      if (!panel || !textPlane || !textPlane.visible || !cam) return;

      const geo = (panel as THREE.Mesh).geometry;
      const posAttr = geo.getAttribute('position');
      const bv0 = v0.current;
      const bv1 = v1.current;
      const bv2 = v2.current;
      bv0.set(posAttr.getX(0), posAttr.getY(0), posAttr.getZ(0));
      bv1.set(posAttr.getX(1), posAttr.getY(1), posAttr.getZ(1));
      bv2.set(posAttr.getX(2), posAttr.getY(2), posAttr.getZ(2));
      if ((panel as THREE.SkinnedMesh).isSkinnedMesh) {
        (panel as THREE.SkinnedMesh).applyBoneTransform(0, bv0);
        (panel as THREE.SkinnedMesh).applyBoneTransform(1, bv1);
        (panel as THREE.SkinnedMesh).applyBoneTransform(2, bv2);
      }
      panel.updateMatrixWorld(true);
      panel.localToWorld(bv0);
      panel.localToWorld(bv1);
      panel.localToWorld(bv2);
      edge1.current.subVectors(bv1, bv0);
      edge2.current.subVectors(bv2, bv0);
      faceNormal.current.crossVectors(edge1.current, edge2.current).normalize();
      boxPrecise.current.setFromObject(panel, true);
      if (boxPrecise.current.isEmpty()) return;
      boxPrecise.current.getCenter(centerVec.current);
      boxPrecise.current.getSize(sizeVec.current);
      toCamera.current.subVectors(cam.position, centerVec.current).normalize();
      if (faceNormal.current.dot(toCamera.current) < 0) {
        faceNormal.current.negate();
      }
      const d = worldUp.current.dot(faceNormal.current);
      faceUp.current.copy(worldUp.current).addScaledVector(faceNormal.current, -d).normalize();
      faceRight.current.crossVectors(faceUp.current, faceNormal.current).normalize();
      rotMat.current.makeBasis(faceRight.current, faceUp.current, faceNormal.current);
      textPlane.quaternion.setFromRotationMatrix(rotMat.current);
      textPlane.position.copy(centerVec.current);
      const dims = [sizeVec.current.x, sizeVec.current.y, sizeVec.current.z].sort((a, b) => b - a);
      panelDownWorld.current.set(0, -1, 0);
      panelDownWorld.current.transformDirection(panel.matrixWorld);
      shiftVec.current.copy(panelDownWorld.current).addScaledVector(
        faceRight.current,
        -panelDownWorld.current.dot(faceRight.current)
      );
      if (shiftVec.current.lengthSq() < 1e-6) shiftVec.current.copy(panelDownWorld.current);
      shiftVec.current.normalize();
      textPlane.position.addScaledVector(
        shiftVec.current,
        Math.max(dims[0], dims[1]) * SPRITE_SHIFT_ALONG_PANEL_DOWN
      );
      textPlane.position.addScaledVector(faceNormal.current, TEXT_PLANE_SURFACE_OFFSET);
      const inset = PANEL_TEXT_INSET;
      const faceW = Math.max(dims[0], 0.001) * inset;
      const faceH = Math.max(dims[1], 0.001) * inset;
      textPlane.scale.set(faceW, faceH, 1);
    }, []);

    const onLoadRef = useRef(onLoad);
    const onErrorRef = useRef(onError);
    const onBeforeRebuildRef = useRef(onBeforeRebuild);
    onLoadRef.current = onLoad;
    onErrorRef.current = onError;
    onBeforeRebuildRef.current = onBeforeRebuild;

    useEffect(() => {
      if (!containerRef.current) return;
      const container = containerRef.current;
      const sleep = (ms: number) => new Promise<void>((resolve) => window.setTimeout(resolve, ms));
      const withRetry = async <T,>(
        label: string,
        run: (attempt: number) => Promise<T>,
        maxRetries = ASSET_MAX_RETRIES
      ): Promise<T> => {
        let lastError: unknown = null;
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          try {
            if (attempt > 1) {
              console.warn(`[Character2][AssetRetry] ${label} retry ${attempt}/${maxRetries}`);
            }
            if (DEBUG_FORCE_FAIL_ALL_RETRY_ATTEMPTS) {
              throw new Error(`[Character2][Debug] Forced failure for ${label} attempt ${attempt}`);
            }
            return await run(attempt);
          } catch (err) {
            lastError = err;
            if (attempt >= maxRetries) break;
            await sleep(ASSET_RETRY_BASE_DELAY_MS * attempt);
          }
        }
        throw lastError instanceof Error
          ? lastError
          : new Error(`[Character2][AssetRetry] ${label} failed after ${maxRetries} attempts`);
      };
      const withBust = (url: string, attempt: number) =>
        attempt <= 1 ? url : `${url}${url.includes('?') ? '&' : '?'}retry=${attempt}&ts=${Date.now()}`;
      const dracoLoader = new DRACOLoader();
      // Local decoders (public/draco) — same build as three/examples/jsm/libs/draco/gltf; not related to animation binding.
      // Absolute path required for Capacitor WebView (capacitor://localhost) to resolve correctly.
      dracoLoader.setDecoderPath(`${process.env.PUBLIC_URL || ''}/draco/`);
      // WKWebView (iOS) can fail to instantiate WASM; JS-only decoder is safer for cross-platform.
      dracoLoader.setDecoderConfig({ type: 'js' });
      const gltfLoader = new GLTFLoader();
      gltfLoader.setDRACOLoader(dracoLoader);

      const w0 = container.clientWidth || 300;
      const h0 = typeof height === 'number' ? height : container.clientHeight || 200;

      const scene = new THREE.Scene();
      scene.background = null;
      sceneRef.current = scene;

      const mainCamera = new THREE.PerspectiveCamera(50, w0 / h0, 0.05, 2000);
      cameraRef.current = mainCamera;

      const renderer = new THREE.WebGLRenderer({
        antialias: true,
        powerPreference: 'high-performance',
        alpha: true,
      });
      renderer.setSize(w0, h0);
      renderer.setPixelRatio(window.devicePixelRatio);
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.setClearColor(0x000000, 0);
      renderer.domElement.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;';
      container.appendChild(renderer.domElement);
      rendererRef.current = renderer;

      // WebGL context loss/restore: full scene rebuild on GPU recovery (iOS backgrounding, etc.)
      const canvas = renderer.domElement;
      const onContextLost = (e: Event) => {
        e.preventDefault();
        contextLostRef.current = true;
        cancelAnimationFrame(animationFrameRef.current);
        console.log('[Character2] WebGL context lost — animation paused, awaiting restore');
      };
      const onContextRestored = () => {
        console.log('[Character2] WebGL context restored — scheduling full scene rebuild');
        contextLostRef.current = false;
        if (rebuildTimerRef.current !== null) window.clearTimeout(rebuildTimerRef.current);
        onBeforeRebuildRef.current?.();
        rebuildTimerRef.current = window.setTimeout(() => {
          rebuildTimerRef.current = null;
          setSceneVersion((v) => v + 1);
        }, 800);
      };
      canvas.addEventListener('webglcontextlost', onContextLost);
      canvas.addEventListener('webglcontextrestored', onContextRestored);

      const renderPass = new RenderPass(scene, mainCamera);
      const bloomPass = new UnrealBloomPass(
        new THREE.Vector2(w0, h0),
        VISUAL_SETTINGS.bloomStrength,
        0.4,
        0.85
      );
      bloomPass.threshold = VISUAL_SETTINGS.bloomThreshold;
      bloomPassRef.current = bloomPass;
      const outputPass = new OutputPass();
      const composer = new EffectComposer(renderer);
      composer.addPass(renderPass);
      composer.addPass(bloomPass);
      composer.addPass(outputPass);
      composerRef.current = composer;

      // HDR-only lighting pipeline: environment map drives IBL.
      // No explicit scene lights here; GLB + HDR control the look.
      const loadAll = async () => {
        try {
          const envMap = await withRetry('env_hdr_exr', async (attempt) => {
            const pmremGenerator = new THREE.PMREMGenerator(renderer);
            pmremGenerator.compileEquirectangularShader();
            const exrLoader = new EXRLoader();
            const hdrTexture = await new Promise<THREE.DataTexture>((resolve, reject) => {
              exrLoader.load(withBust(URL_ENV_HDR, attempt), resolve, undefined, reject);
            });
            try {
              hdrTexture.mapping = THREE.EquirectangularReflectionMapping;
              return pmremGenerator.fromEquirectangular(hdrTexture).texture;
            } finally {
              hdrTexture.dispose();
              pmremGenerator.dispose();
            }
          });
          scene.environment = envMap;
          console.log('[Character2][Assets] env hdr ready');

          const [faceJson, bgGltf, charGltf, signGltf, loadingDotsGltf] = await Promise.all([
            withRetry('face_json', async (attempt) => {
              const response = await fetch(withBust(URL_FACE_JSON, attempt), { cache: 'no-store' });
              if (!response.ok) {
                throw new Error(`[Character2] face.json http status=${response.status}`);
              }
              return response.json();
            }),
            withRetry('background_glb', async (attempt) => {
              return new Promise<any>((resolve, reject) => {
                gltfLoader.load(
                  withBust(URL_BACKGROUND, attempt),
                  resolve,
                  undefined,
                  (e) => {
                    console.error('[Character2] FAIL background', e);
                    reject(e);
                  }
                );
              });
            }),
            withRetry('character_glb', async (attempt) => {
              return new Promise<any>((resolve, reject) => {
                gltfLoader.load(
                  withBust(URL_CHARACTER, attempt),
                  resolve,
                  undefined,
                  (e) => {
                    console.error('[Character2] FAIL character', e);
                    reject(e);
                  }
                );
              });
            }),
            withRetry('sign_glb', async (attempt) => {
              return new Promise<any>((resolve, reject) => {
                gltfLoader.load(
                  withBust(URL_SIGN, attempt),
                  resolve,
                  undefined,
                  (e) => {
                    console.error('[Character2] FAIL sign', e);
                    reject(e);
                  }
                );
              });
            }),
            withRetry('loading_dots_glb', async (attempt) => {
              return new Promise<any>((resolve, reject) => {
                gltfLoader.load(
                  withBust(URL_LOADING_DOTS, attempt),
                  resolve,
                  undefined,
                  (e) => {
                    console.error('[Character2] FAIL loading dots', e);
                    reject(e);
                  }
                );
              });
            }),
          ]);

          faceDataRef.current = faceJson;
          console.log('[Character2][Assets] face json ready');

          const bgRoot = bgGltf.scene;
          scene.add(bgRoot);
          bgRoot.traverse((child: THREE.Object3D) => {
            if ((child as THREE.Camera).isCamera) {
              child.visible = false;
            }
          });

          // Find "Camera Left out" (front/default) and "Camera Left in" (sign-focused) from the background GLB.
          // GLTFLoader sanitizes names: "Camera Left in" → "Camera_Left_in", etc.
          let camLeftIn: THREE.PerspectiveCamera | null = null;
          let camLeftOut: THREE.PerspectiveCamera | null = null;
          bgRoot.traverse((obj) => {
            const c = obj as THREE.PerspectiveCamera;
            if (!c.isPerspectiveCamera) return;
            const n = (c.name || '').toLowerCase();
            if (n.includes('left') && n.includes('in') && !camLeftIn) camLeftIn = c;
            if (n.includes('left') && n.includes('out') && !camLeftOut) camLeftOut = c;
          });
          cameraLeftInRef.current = camLeftIn;
          cameraLeftOutRef.current = camLeftOut;

          // Camera animation clips from background GLB (same pattern as boss's prototype)
          if (bgGltf.animations?.length) {
            const bgMixer = new THREE.AnimationMixer(bgRoot);
            bgMixerRef.current = bgMixer;
            bgGltf.animations.forEach((clip: THREE.AnimationClip) => {
              const n = clip.name.toLowerCase();
              if (n.includes('left') && n.includes('in')) camClipLeftInRef.current = clip;
              if (n.includes('left') && n.includes('out')) camClipLeftOutRef.current = clip;
            });
          }

          // Default camera: "Camera Left in" at rest = frontal view. "Left out" rest position is rotated toward sign.
          let srcCam = camLeftIn;
          if (!srcCam) srcCam = pickCamera003(bgRoot);
          if (!srcCam) srcCam = pickAnyPerspectiveCamera(bgRoot);
          if (!srcCam) {
            throw new Error('[Character2] No perspective camera in background GLB');
          }
          sourceCameraRef.current = srcCam;
          syncCameraFromGltf(mainCamera, srcCam, w0 / h0);

          const charRoot = charGltf.scene;
          charRoot.visible = false;
          scene.add(charRoot);
          // Same issue as ":" in tracks — Blender bone names must not contain ":" for PropertyBinding.
          renameObjectNamesColonToUnderscore(charRoot);

          // CRITICAL: THREE.PropertyBinding resolves bone tracks via skeleton.getBoneByName only when
          // AnimationMixer root is a SkinnedMesh. Using charRoot (Group) breaks newQuan (Lombardi) binding → T-pose.
          const skinnedMeshRoot = pickPrimarySkinnedMesh(charRoot);
          const mixerRoot = skinnedMeshRoot ?? charRoot;
          if (!skinnedMeshRoot) {
            console.warn('[Character2] No SkinnedMesh found; animation binding may fail like CharacterEmbed edge cases');
          } else {
            console.log(
              `[Character2] AnimationMixer root = SkinnedMesh "${skinnedMeshRoot.name}" (${skinnedMeshRoot.skeleton.bones.length} bones) — matches THREE skin binding rules`
            );
          }

          // DEBUG: log actual bone names so we can match FBX tracks correctly
          const skelBones = skinnedMeshRoot?.skeleton?.bones;
          if (skelBones) {
            console.log('[Character2] Skeleton bone names (first 5):', skelBones.slice(0, 5).map((b: THREE.Bone) => b.name));
            console.log('[Character2] Skeleton bone count:', skelBones.length);
          }
          const sceneNames: string[] = [];
          charRoot.traverse((c: any) => { if (c.isBone) sceneNames.push(c.name); });
          console.log('[Character2] Scene bone names (first 5):', sceneNames.slice(0, 5));

          animationsRef.current = {};
          const charMixer = new THREE.AnimationMixer(mixerRoot);
          mixerRef.current = charMixer;

          // Same idea as CharacterEmbed: FBX Idle/Talking + strip positions + Mixamo name remap.
          // newQuan GLB clips are usually bind pose; load FBX first so we never lock to T-pose.
          const loadFbxIdleTalking = async () => {
            const fbxLoader = new FBXLoader();
            for (const animName of ['Idle']) {
              // for (const animName of ['Idle', 'Talking']) {
              try {
                const fbx = await new Promise<any>((resolve, reject) => {
                  fbxLoader.load(`/character/animations/${animName}.fbx`, resolve, undefined, reject);
                });
                if (fbx.animations?.[0]) {
                  const clip = fbx.animations[0].clone();
                  clip.tracks = clip.tracks.filter(
                    (track: THREE.KeyframeTrack) => !track.name.includes('.position')
                  );
                  clip.tracks.forEach((track: THREE.KeyframeTrack) => {
                    track.name = remapMixamoFbxTrackName(track.name);
                  });
                  console.log(`[Character2] FBX "${animName}" remapped tracks (first 3):`, clip.tracks.slice(0, 3).map((t: THREE.KeyframeTrack) => t.name));
                  filterAnimationTracksForRig(clip, mixerRoot, skinnedMeshRoot);
                  if (clip.tracks.length === 0) {
                    console.warn(`[Character2] FBX "${animName}" has no tracks bound to this rig — check bone names`);
                    continue;
                  }
                  const action = charMixer.clipAction(clip);
                  action.setLoop(THREE.LoopRepeat, Infinity);
                  action.clampWhenFinished = false;
                  animationsRef.current[animName] = action;
                  console.log(`[Character2] FBX "${animName}" playing on skinned root (${clip.tracks.length} tracks)`);
                }
              } catch (e) {
                console.warn(`[Character2] FBX "${animName}" failed`, e);
              }
            }
          };

          await loadFbxIdleTalking();

          if (!animationsRef.current['Idle'] && charGltf.animations?.length) {
            charGltf.animations.forEach((clip: THREE.AnimationClip, index: number) => {
              const c = clip.clone();
              sanitizeGltfClipTrackColons(c);
              filterAnimationTracksForRig(c, mixerRoot, skinnedMeshRoot);
              const animName = c.name || `Animation_${index}`;
              const action = charMixer.clipAction(c);
              animationsRef.current[animName] = action;
              if ((c.name || '').toLowerCase().includes('idle') || index === 0) {
                animationsRef.current['Idle'] = action;
              }
            });
            console.log('[Character2] GLB clips as Idle fallback');
          }

          let headBone: THREE.Object3D | null = null;
          charRoot.traverse((child: any) => {
            if (
              child.name === 'mixamorig_Head' ||
              (child.isBone && String(child.name).toLowerCase().includes('head'))
            ) {
              if (!headBone || child.isBone) headBone = child;
            }
          });
          let targetParent: THREE.Object3D = charRoot;
          if (headBone) targetParent = headBone;
          headBoneRef.current = headBone;

          if (faceJson) {
            const ledGeo = new THREE.SphereGeometry(0.012, 12, 12);
            const ledMat = new THREE.MeshStandardMaterial({
              color: VISUAL_SETTINGS.ledColor,
              emissive: VISUAL_SETTINGS.ledEmissiveColor,
              emissiveIntensity: VISUAL_SETTINGS.ledEmissiveIntensity,
              flatShading: false,
            });
            const ledMesh = new THREE.InstancedMesh(ledGeo, ledMat, LED_COUNT);
            ledMeshRef.current = ledMesh;
            const spacing = 0.015;
            const offset = (GRID_SIZE * spacing) / 2;
            for (let i = 0; i < LED_COUNT; i++) {
              const x = (i % GRID_SIZE) * spacing - offset;
              const y = -(Math.floor(i / GRID_SIZE) * spacing - offset);
              dummyRef.current.position.set(x, y, 0);
              dummyRef.current.scale.set(0, 0, 0);
              dummyRef.current.updateMatrix();
              ledMesh.setMatrixAt(i, dummyRef.current.matrix);
            }
            const faceGroup = new THREE.Group();
            faceGroup.add(ledMesh);
            faceGroup.position.set(
              VISUAL_SETTINGS.facePosition.x,
              VISUAL_SETTINGS.facePosition.y,
              VISUAL_SETTINGS.facePosition.z
            );
            faceGroup.scale.set(
              VISUAL_SETTINGS.faceScale.x,
              VISUAL_SETTINGS.faceScale.y,
              VISUAL_SETTINGS.faceScale.z
            );
            targetParent.add(faceGroup);
          }

          // Loading dots GLB — added to scene (world-space, same as character/sign)
          const loadingDotsRoot = loadingDotsGltf.scene;
          loadingDotsRoot.position.x = 0.012;
          loadingDotsRoot.position.y = 0.012;
          loadingDotsRoot.visible = false;
          loadingDotsRoot.traverse((child) => {
            if ((child as THREE.Mesh).isMesh) {
              (child as THREE.Mesh).frustumCulled = false;
            }
          });
          scene.add(loadingDotsRoot);
          targetParent.attach(loadingDotsRoot);
          loadingDotsRootRef.current = loadingDotsRoot;
          if (loadingDotsGltf.animations?.length) {
            const dotsMixer = new THREE.AnimationMixer(loadingDotsRoot);
            loadingDotsGltf.animations.forEach((clip: THREE.AnimationClip) => {
              const action = dotsMixer.clipAction(clip);
              action.setLoop(THREE.LoopRepeat, Infinity);
              action.clampWhenFinished = false;
              action.play();
            });
            loadingDotsMixerRef.current = dotsMixer;
          }

          if (animationsRef.current['Idle']) {
            animationsRef.current['Idle'].reset().play();
          }
          // Reveal character after Idle pose is applied (prevents T-pose flash on rebuild).
          // A single mixer tick settles the skeleton into the Idle pose before the first visible frame.
          if (mixerRef.current) {
            mixerRef.current.update(0);
          }
          charRoot.visible = true;

          const signRoot = signGltf.scene;
          scene.add(signRoot);
          signRoot.visible = false;
          signRootRef.current = signRoot;

          const signMixer = new THREE.AnimationMixer(signRoot);
          signMixerRef.current = signMixer;
          signActionsRef.current = {};
          signGltf.animations.forEach((clip: THREE.AnimationClip) => {
            if (clip.name === 'CameraAction') return;
            const action = signMixer.clipAction(clip);
            action.clampWhenFinished = true;
            action.setLoop(THREE.LoopOnce, 1);
            signActionsRef.current[clip.name] = action;
          });
          if (signActionsRef.current['Sign Idle Hold']) {
            signActionsRef.current['Sign Idle Hold'].setLoop(THREE.LoopRepeat, Infinity);
            signActionsRef.current['Sign Idle Hold'].clampWhenFinished = false;
          }

          let panelMesh: THREE.SkinnedMesh | THREE.Mesh | null = null;
          signRoot.traverse((child) => {
            if (child.name === 'Sign.002' && (child as THREE.Mesh).isMesh) {
              panelMesh = child as THREE.SkinnedMesh;
            }
          });
          if (!panelMesh) {
            signRoot.traverse((child) => {
              const m = child as THREE.Mesh;
              if (!panelMesh && m.isMesh && (m.name || '').toLowerCase().includes('sign')) {
                panelMesh = m as THREE.SkinnedMesh;
              }
            });
          }
          if (panelMesh) {
            signPanelRef.current = panelMesh;

            const { canvas, texture } = createSignTextCanvasTexture();
            signTextCanvasRef.current = canvas;
            signTextTextureRef.current = texture;
            if ('fonts' in document) {
              await document.fonts.ready;
              try {
                await document.fonts.load('900 96px "Roboto"');
              } catch {
                /* ignore */
              }
            }
            updateSignTextTexture(canvas, texture, signTextPropRef.current);
            const planeMat = new THREE.MeshBasicMaterial({
              map: texture,
              transparent: true,
              depthTest: true,
              depthWrite: false,
              side: THREE.DoubleSide,
              toneMapped: false,
            });
            const textPlane = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), planeMat);
            textPlane.renderOrder = 999;
            textPlane.visible = false;
            scene.add(textPlane);
            signTextPlaneRef.current = textPlane;
          }

          signReadyRef.current = true;
          prevSignVisibleRef.current = false;
          applySignVisibility(!!showSignRef.current);

          console.log('[Character2][Assets] critical assets loaded successfully');
          onLoadRef.current?.();
        } catch (err: any) {
          console.error('[Character2] load failed', err);
          onErrorRef.current?.(err instanceof Error ? err : new Error(String(err)));
        }
      };

      void loadAll();

      const animate = () => {
        animationFrameRef.current = requestAnimationFrame(animate);
        try {
          const delta = clockRef.current.getDelta();
          const safeDelta = Math.min(delta, 0.1);
          if (mixerRef.current) mixerRef.current.update(safeDelta);
          if (signMixerRef.current) signMixerRef.current.update(safeDelta);
          if (loadingDotsMixerRef.current) loadingDotsMixerRef.current.update(safeDelta);
          if (bgMixerRef.current) bgMixerRef.current.update(safeDelta);

          if (containerRef.current && cameraRef.current && sourceCameraRef.current) {
            const w = containerRef.current.clientWidth || 300;
            const h = containerRef.current.clientHeight || 200;
            syncCameraFromGltf(cameraRef.current, sourceCameraRef.current, w / Math.max(1, h));
          }

          // Hide sign while Quan is speaking; restore when audio ends
          // Must mirror the same talking detection as updateMouth: PCM streaming OR HTML audio
          const _quanIsTalking = isTalkingRef.current || playbackStartTimeRef.current !== null || (audioRef.current && !audioRef.current.paused);
          if (signReadyRef.current) {
            if (_quanIsTalking && prevSignVisibleRef.current && !signSuppressedByAudioRef.current) {
              signSuppressedByAudioRef.current = true;
              applySignVisibility(false);
            } else if (!_quanIsTalking && signSuppressedByAudioRef.current) {
              signSuppressedByAudioRef.current = false;
              if (showSignRef.current) {
                applySignVisibility(true);
              }
            }
          }

          updateSignTextPlaneFollow();

          const t = performance.now() / 1000;
          try {
            updateMouth(t);
          } catch (e) {
            console.warn('[Character2] mouth update', e);
          }

          if (composerRef.current) {
            composerRef.current.render();
          } else if (rendererRef.current && sceneRef.current && cameraRef.current) {
            rendererRef.current.render(sceneRef.current, cameraRef.current);
          }
        } catch (e) {
          console.error('[Character2] animate', e);
        }
      };
      animate();

      const handleResize = () => {
        if (!containerRef.current || !cameraRef.current || !rendererRef.current) return;
        const w = containerRef.current.clientWidth;
        const h = containerRef.current.clientHeight;
        if (w <= 0 || h <= 0) return;
        cameraRef.current.aspect = w / h;
        cameraRef.current.updateProjectionMatrix();
        rendererRef.current.setSize(w, h);
        if (composerRef.current) composerRef.current.setSize(w, h);
        if (bloomPassRef.current) bloomPassRef.current.setSize(w, h);
      };

      const ro = new ResizeObserver(() => {
        requestAnimationFrame(handleResize);
      });
      ro.observe(container);
      window.addEventListener('resize', handleResize);

      return () => {
        ro.disconnect();
        window.removeEventListener('resize', handleResize);
        cancelAnimationFrame(animationFrameRef.current);
        // Remove WebGL context listeners before disposing renderer
        canvas.removeEventListener('webglcontextlost', onContextLost);
        canvas.removeEventListener('webglcontextrestored', onContextRestored);
        if (rebuildTimerRef.current !== null) {
          window.clearTimeout(rebuildTimerRef.current);
          rebuildTimerRef.current = null;
        }
        contextLostRef.current = false;
        dracoLoader.dispose();
        signReadyRef.current = false;
        if (bgMixerRef.current) { bgMixerRef.current.stopAllAction(); bgMixerRef.current = null; }
        if (audioRef.current && audioEndHandlerRef.current) {
          audioRef.current.removeEventListener('ended', audioEndHandlerRef.current);
        }
        const tp = signTextPlaneRef.current;
        signTextPlaneRef.current = null;
        if (tp) {
          tp.geometry.dispose();
          (tp.material as THREE.MeshBasicMaterial).dispose();
          scene.remove(tp);
        }
        if (signTextTextureRef.current) {
          signTextTextureRef.current.dispose();
          signTextTextureRef.current = null;
        }
        signTextCanvasRef.current = null;
        if (loadingDotsMixerRef.current) {
          loadingDotsMixerRef.current.stopAllAction();
          loadingDotsMixerRef.current = null;
        }
        if (loadingDotsRootRef.current) {
          // scene.remove(loadingDotsRootRef.current);
          loadingDotsRootRef.current.parent?.remove(loadingDotsRootRef.current);
          loadingDotsRootRef.current = null;
        }
        if (rendererRef.current && containerRef.current) {
          try {
            containerRef.current.removeChild(rendererRef.current.domElement);
          } catch {
            /* */
          }
          rendererRef.current.dispose();
        }
      };

    }, [sceneVersion]);

    // Rebuild scene when app/tab returns from background (mobile web browsers only).
    // On Capacitor native, webglcontextlost/restored handlers (above) already trigger the rebuild
    // when the GPU process dies. Adding a visibility-based rebuild there causes double/triple rebuilds
    // that spike memory and can crash the WKWebView process (iOS kills it under memory pressure).
    useEffect(() => {
      const isNative = !!(window as any).Capacitor?.isNativePlatform?.();
      if (isNative) return; // Native: rely on webglcontextlost/restored only
      if (!isMobile) return; // Desktop: not needed

      const scheduleRebuild = () => {
        if (rebuildTimerRef.current !== null) window.clearTimeout(rebuildTimerRef.current);
        onBeforeRebuildRef.current?.();
        rebuildTimerRef.current = window.setTimeout(() => {
          rebuildTimerRef.current = null;
          if (contextLostRef.current) {
            console.log('[Character2] Skipping rebuild — context still lost, waiting for restore event');
            return;
          }
          setSceneVersion((v) => v + 1);
        }, 400);
      };

      const onVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          console.log('[Character2] Tab/app visible — scheduling full scene rebuild for visual recovery');
          scheduleRebuild();
        }
      };
      const onPageShow = (e: PageTransitionEvent) => {
        if (e.persisted || document.visibilityState === 'visible') {
          console.log('[Character2] Page restored from bfcache — scheduling full scene rebuild');
          scheduleRebuild();
        }
      };

      document.addEventListener('visibilitychange', onVisibilityChange);
      window.addEventListener('pageshow', onPageShow);

      return () => {
        document.removeEventListener('visibilitychange', onVisibilityChange);
        window.removeEventListener('pageshow', onPageShow);
      };
    }, []);

    return (
      <Box
        ref={containerRef}
        sx={{
          width: '100%',
          height: height,
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 0,
          backgroundColor: backgroundColor,
        }}
      />
    );
  }
);

CharacterEmbed2.displayName = 'CharacterEmbed2';

export default CharacterEmbed2;
